import type { audioDeviceManager } from '../AudioDeviceManager';
import type { BrowserPhoneConfig, WebRtcMediaQuality } from '../types';
import type { SipLogger, SipTimerPort } from './contracts';
import { MediaQualityMonitor } from './MediaQualityMonitor';

const TURN_PROBE_TIMEOUT_MS = 20_000;
const TURN_PROBE_CACHE_MS = 120_000;

export function configuredTurnServers(
  config: BrowserPhoneConfig,
): RTCIceServer[] {
  const source = Array.isArray(config.iceServers) ? config.iceServers : [];
  const servers: RTCIceServer[] = [];

  for (const server of source) {
    if (!server) continue;
    const urls = Array.isArray(server.urls) ? server.urls : [server.urls];

    for (const rawUrl of urls) {
      const url = String(rawUrl || '').trim();
      if (!/^turns?:/i.test(url)) continue;
      if (!server.username || !server.credential) continue;

      servers.push({
        urls: url,
        username: String(server.username),
        credential: String(server.credential),
      });
    }
  }

  if (!servers.length) {
    throw new Error(
      'TURN relay не настроен. В конфигурации нет TURN URL с логином и паролем.',
    );
  }

  return servers;
}

export function browserPhoneRtcConfig(
  config: BrowserPhoneConfig,
): RTCConfiguration {
  return {
    iceServers: configuredTurnServers(config),
    iceTransportPolicy: 'relay',
    bundlePolicy: 'max-bundle',
    rtcpMuxPolicy: 'require',
    iceCandidatePoolSize: 0,
  };
}

type AudioDevices = Pick<typeof audioDeviceManager,
  | 'getActiveStream'
  | 'prepareCallStream'
  | 'releaseCallStream'
  | 'bindOutputElement'
  | 'applyOutputDevice'
  | 'markCallInProgress'>;

export interface SipMediaControllerDeps {
  audioDevices: AudioDevices;
  timers: SipTimerPort;
  now: () => number;
  createPeerConnection: (config: RTCConfiguration) => RTCPeerConnection;
  createMediaStream: (tracks: MediaStreamTrack[]) => MediaStream;
  onActivityChange: () => void;
  onQuality: (quality: WebRtcMediaQuality) => void;
  onWarning: (message: string) => void;
  logger: SipLogger;
}

export class SipMediaController {
  private readonly audioDevices: AudioDevices;
  private readonly timers: SipTimerPort;
  private readonly now: () => number;
  private readonly createPeerConnection: (
    config: RTCConfiguration,
  ) => RTCPeerConnection;
  private readonly createMediaStream: (
    tracks: MediaStreamTrack[],
  ) => MediaStream;
  private readonly onActivityChange: () => void;
  private readonly logger: SipLogger;
  private readonly quality: MediaQualityMonitor;
  private localStream: MediaStream | null = null;
  private microphoneRequestPending = false;
  private microphoneInFlight = 0;
  private lifecycleGeneration = 0;
  private releaseNeeded = false;
  private finishStopRequested = false;
  private audioElement: HTMLAudioElement | null = null;
  private remoteSession: any = null;
  private remoteConnection: RTCPeerConnection | null = null;
  private remoteTrackListener: (() => void) | null = null;
  private remoteGeneration = 0;
  private turnProbePromise: Promise<void> | null = null;
  private turnProbeCancel: (() => void) | null = null;
  private turnProbeSucceededAt = 0;

  constructor(deps: SipMediaControllerDeps) {
    this.audioDevices = deps.audioDevices;
    this.timers = deps.timers;
    this.now = deps.now;
    this.createPeerConnection = deps.createPeerConnection;
    this.createMediaStream = deps.createMediaStream;
    this.onActivityChange = deps.onActivityChange;
    this.logger = deps.logger;
    this.quality = new MediaQualityMonitor({
      timers: deps.timers,
      now: deps.now,
      getActiveStream: () => this.audioDevices.getActiveStream(),
      onQuality: deps.onQuality,
      onWarning: deps.onWarning,
      logger: deps.logger,
    });
  }

  async prepareCallStream(): Promise<MediaStream> {
    const active = this.audioDevices.getActiveStream();
    const activeTrack = this.liveAudioTrack(active);
    if (active && activeTrack) {
      this.finishStopRequested = false;
      this.releaseNeeded = true;
      activeTrack.enabled = true;
      this.localStream = active;
      this.onActivityChange();
      return active;
    }

    const generation = this.lifecycleGeneration;
    this.finishStopRequested = false;
    this.releaseNeeded = true;
    this.microphoneInFlight += 1;
    this.microphoneRequestPending = true;
    this.onActivityChange();

    try {
      const prepared = await this.audioDevices.prepareCallStream();
      if (generation !== this.lifecycleGeneration) {
        this.releaseStream(false);
        throw new Error('Подготовка микрофона отменена после остановки телефона.');
      }

      const microphoneTrack = this.liveAudioTrack(prepared);
      if (!microphoneTrack) {
        this.releaseStream();
        throw new Error('Микрофон не создал активную аудиодорожку.');
      }

      microphoneTrack.enabled = true;
      this.localStream = prepared;
      this.onActivityChange();
      return prepared;
    } finally {
      this.microphoneInFlight = Math.max(0, this.microphoneInFlight - 1);
      if (generation === this.lifecycleGeneration) {
        this.microphoneRequestPending = false;
        this.onActivityChange();
      } else if (this.finishStopRequested && this.microphoneInFlight === 0) {
        this.releaseStream(false);
      }
    }
  }

  async requestMicrophone(): Promise<void> {
    await this.prepareCallStream();
  }

  markCallInProgress(): void {
    this.audioDevices.markCallInProgress(true);
  }

  cancelPreparedMicrophone(hasSession: boolean): void {
    if (hasSession) return;
    this.lifecycleGeneration += 1;
    this.microphoneRequestPending = false;
    this.finishStopRequested = true;
    if (this.microphoneInFlight === 0) this.releaseStream();
    this.onActivityChange();
  }

  bindAudioElement(element: HTMLAudioElement | null): void {
    this.audioElement = element;
    this.audioDevices.bindOutputElement(element);
    if (this.remoteSession) this.attachRemoteAudio(this.remoteSession);
  }

  attachRemoteAudio(session: any): void {
    const connection: RTCPeerConnection | undefined = session?.connection;
    this.detachRemoteAudio();
    this.remoteSession = session;
    if (!connection) return;

    this.remoteConnection = connection;
    const generation = this.remoteGeneration;
    const update = () => {
      if (
        generation !== this.remoteGeneration
        || this.remoteSession !== session
        || this.remoteConnection !== connection
      ) {
        return;
      }

      const audio = this.audioElement;
      if (!audio) return;
      const tracks = connection.getReceivers?.()
        .map(receiver => receiver.track)
        .filter(
          (item): item is MediaStreamTrack => Boolean(
            item
            && item.kind === 'audio'
            && item.readyState !== 'ended',
          ),
        ) || [];
      if (!tracks.length) return;

      audio.autoplay = true;
      audio.muted = false;
      audio.volume = 1;
      audio.setAttribute('playsinline', '');

      const current = audio.srcObject as MediaStream | null;
      const currentIds = current?.getAudioTracks().map(item => item.id) || [];
      const nextIds = tracks.map(item => item.id);
      if (
        !current
        || currentIds.length !== nextIds.length
        || nextIds.some(id => !currentIds.includes(id))
      ) {
        audio.srcObject = this.createMediaStream(tracks);
      }

      void this.audioDevices.applyOutputDevice().catch(() => undefined);
      void audio.play().catch(error => {
        this.logger.warn('[telephony] remote audio playback blocked', error);
      });
    };

    this.remoteTrackListener = update;
    connection.addEventListener?.('track', update);
    update();
  }

  startQuality(session: any): void {
    this.quality.start(session);
  }

  endSession(): void {
    this.lifecycleGeneration += 1;
    this.microphoneRequestPending = false;
    this.finishStopRequested = true;
    this.detachRemoteAudio();
    this.quality.stop(true);
    if (this.microphoneInFlight === 0) this.releaseStream();
    this.onActivityChange();
  }

  rtcConfiguration(config: BrowserPhoneConfig): RTCConfiguration {
    return browserPhoneRtcConfig(config);
  }

  async ensureTurnRelay(config: BrowserPhoneConfig): Promise<void> {
    if (
      this.turnProbeSucceededAt > 0
      && this.now() - this.turnProbeSucceededAt < TURN_PROBE_CACHE_MS
    ) {
      return;
    }

    if (this.turnProbePromise) {
      await this.turnProbePromise;
      return;
    }

    const generation = this.lifecycleGeneration;
    const probe = this.probeTurnRelay(config);
    this.turnProbePromise = probe;
    try {
      await probe;
      if (generation !== this.lifecycleGeneration) {
        throw new Error('TURN-проверка отменена после остановки телефона.');
      }
      this.turnProbeSucceededAt = this.now();
    } finally {
      if (this.turnProbePromise === probe) this.turnProbePromise = null;
    }
  }

  prepareStop(): void {
    this.lifecycleGeneration += 1;
    this.microphoneRequestPending = false;
    this.finishStopRequested = true;
    this.quality.stop(false);
    this.detachRemoteAudio();
    this.turnProbeCancel?.();
    this.turnProbeCancel = null;
    this.onActivityChange();
  }

  finishStop(): void {
    this.finishStopRequested = true;
    this.turnProbeSucceededAt = 0;
    if (this.microphoneInFlight === 0) this.releaseStream();
  }

  hasActivity(): boolean {
    return Boolean(this.localStream || this.microphoneRequestPending);
  }

  getMediaQuality(): WebRtcMediaQuality {
    return this.quality.getSnapshot();
  }

  private liveAudioTrack(stream: MediaStream | null): MediaStreamTrack | null {
    return stream?.getAudioTracks().find(item => item.readyState === 'live') || null;
  }

  private releaseStream(notify = true): void {
    this.localStream = null;
    this.microphoneRequestPending = false;
    if (this.releaseNeeded) {
      this.releaseNeeded = false;
      this.audioDevices.releaseCallStream();
    }
    if (notify) this.onActivityChange();
  }

  private detachRemoteAudio(): void {
    this.remoteGeneration += 1;
    if (this.remoteConnection && this.remoteTrackListener) {
      this.remoteConnection.removeEventListener?.(
        'track',
        this.remoteTrackListener,
      );
    }
    this.remoteConnection = null;
    this.remoteTrackListener = null;
    this.remoteSession = null;
  }

  private probeTurnRelay(config: BrowserPhoneConfig): Promise<void> {
    const rtcConfig = browserPhoneRtcConfig(config);
    const connection = this.createPeerConnection(rtcConfig);
    const turnUrls = configuredTurnServers(config)
      .flatMap(server => (
        Array.isArray(server.urls) ? server.urls : [server.urls]
      ))
      .map(url => String(url));
    let lastIceError = '';

    const relayInSdp = () => /a=candidate:.*\styp\s+relay(?:\s|$)/im
      .test(String(connection.localDescription?.sdp || ''));

    return new Promise<void>((resolve, reject) => {
      let finished = false;
      let timeout: number | null = null;
      let poll: number | null = null;

      const cleanup = () => {
        if (timeout !== null) this.timers.clearTimeout(timeout);
        if (poll !== null) this.timers.clearInterval(poll);
        connection.removeEventListener('icecandidate', onCandidate);
        connection.removeEventListener('icecandidateerror', onCandidateError);
        connection.removeEventListener(
          'icegatheringstatechange',
          onGatheringState,
        );
        if (this.turnProbeCancel === cancel) this.turnProbeCancel = null;
        connection.close();
      };

      const finish = (error?: Error) => {
        if (finished) return;
        finished = true;
        cleanup();
        if (error) reject(error);
        else resolve();
      };

      const detail = () => (
        lastIceError ? ` Последняя ошибка ICE: ${lastIceError}` : ''
      );
      const cancel = () => finish(
        new Error('TURN-проверка отменена после остановки телефона.'),
      );
      const onCandidate = (event: RTCPeerConnectionIceEvent) => {
        const candidate = String(event.candidate?.candidate || '');
        if (/\styp\s+relay(?:\s|$)/i.test(candidate) || relayInSdp()) {
          finish();
          return;
        }
        if (!event.candidate && connection.iceGatheringState === 'complete') {
          finish(new Error(
            `Браузер не получил TURN relay-кандидат.${detail()}`,
          ));
        }
      };
      const onCandidateError = (event: Event) => {
        const iceError = event as Event & {
          url?: string;
          errorCode?: number;
          errorText?: string;
        };
        const url = String(iceError.url || '').replace(/:[^:@/]+@/, ':***@');
        lastIceError = [
          iceError.errorCode ? `код ${iceError.errorCode}` : '',
          iceError.errorText || '',
          url,
        ].filter(Boolean).join(', ');
      };
      const onGatheringState = () => {
        if (relayInSdp()) {
          finish();
          return;
        }
        if (connection.iceGatheringState === 'complete') {
          finish(new Error(`Сбор ICE завершён без TURN relay.${detail()}`));
        }
      };

      this.turnProbeCancel = cancel;
      connection.addEventListener('icecandidate', onCandidate);
      connection.addEventListener('icecandidateerror', onCandidateError);
      connection.addEventListener('icegatheringstatechange', onGatheringState);
      connection.createDataChannel('intez-turn-probe');

      poll = this.timers.setInterval(() => {
        if (relayInSdp()) finish();
      }, 100);
      timeout = this.timers.setTimeout(() => {
        finish(new Error(
          `TURN relay не получен за 20 секунд. Проверены: ${turnUrls.join(', ')}.${detail()}`,
        ));
      }, TURN_PROBE_TIMEOUT_MS);

      void (async () => {
        try {
          const offer = await connection.createOffer();
          await connection.setLocalDescription(offer);
          if (relayInSdp()) finish();
        } catch (error) {
          finish(new Error(
            error instanceof Error
              ? `Не удалось запустить TURN-проверку: ${error.message}`
              : 'Не удалось запустить TURN-проверку',
          ));
        }
      })();
    });
  }
}
